import type { CorpusConversation } from "@llmnesia/shared-types";
import { Corpus } from "./corpus.js";
import { drainInbox, pendingInboxFiles } from "./ingest.js";
import { SearchIndex, openSearchIndex, type RecentConversation } from "./searchIndex.js";
import type { Hit, SearchFilters } from "./retriever.js";
import { normalizeSaveInput, saveConversationSchema, type SaveConversationInput } from "./saveSchema.js";

export type RecentEntry = RecentConversation & { summary?: string };

/**
 * How long the corpus can go without receiving browser data before reads start
 * carrying a warning. Deliberately generous: the extension pushes within ~60s of
 * a capture, so anything past a couple of days is either a genuinely quiet
 * stretch or a broken pipe — and 48h keeps a quiet weekend from crying wolf.
 */
const STALE_AFTER_HOURS = 48;

export type CorpusStatus = {
  root: string;
  conversationCount: number;
  /**
   * When browser data last landed in this corpus, or null if it never has.
   *
   * Note what this does NOT mean: the extension skips the native host entirely
   * when its delta is empty (see `syncToCorpusViaNativeHost`), so a healthy but
   * idle pipe and a dead one look identical from here. This is "when new content
   * last arrived", not "when sync last ran".
   */
  lastIngestAt: number | null;
  /** Hours since {@link lastIngestAt}, one decimal. Null when never ingested. */
  hoursSinceIngest: number | null;
  /** `updatedAt` of the newest conversation in the index. */
  newestConversationAt: number | null;
  /** Present only past {@link STALE_AFTER_HOURS} — absence means "looks current". */
  staleWarning?: string;
};

export type SaveResult = {
  docId: string;
  outcome: "created" | "updated" | "skipped";
  messageCount: number;
};

export type CompanionServiceOptions = {
  /**
   * Called when an inbox drain fails. Reads degrade to serving the index we
   * already have, so one bad delta file can't take the MCP server down.
   */
  onDrainError?: (error: unknown) => void;
};

/**
 * Read/write facade over the corpus and its search index, shared by the CLI and
 * the MCP server. `saveConversation` writes the corpus AND upserts the index in
 * one path — the same effect an inbox ingest has, which is what makes MCP an
 * ingestion route rather than a read-only query layer.
 *
 * Reads drain `inbox/` first (see {@link refresh}), so the corpus is current as
 * of the query without a watcher process to install or supervise.
 */
export class CompanionService {
  // Serialize drains so concurrent reads queue behind one ingest rather than
  // racing to consume the same file (mirrors Corpus.enqueueWrite).
  private refreshChain: Promise<void> = Promise.resolve();

  private constructor(
    readonly corpus: Corpus,
    readonly index: SearchIndex,
    private readonly onDrainError: (error: unknown) => void
  ) {}

  static async open(root: string, options: CompanionServiceOptions = {}): Promise<CompanionService> {
    const corpus = new Corpus(root);
    const index = await openSearchIndex(corpus);
    const service = new CompanionService(corpus, index, options.onDrainError ?? noop);
    // Never come up stale: the extension may have pushed deltas while we were
    // not running. Bulk lands here so per-query drains only ever see a delta.
    await service.refresh();
    return service;
  }

  /**
   * Ingest whatever is waiting in `inbox/`. Never throws: a corrupt or
   * unreadable delta is reported and skipped rather than failing the read.
   */
  refresh(): Promise<void> {
    const run = this.refreshChain.then(
      () => this.drainPending(),
      () => this.drainPending()
    );
    // Keep the chain alive even if a drain rejects.
    this.refreshChain = run.then(
      () => undefined,
      () => undefined
    );
    return run;
  }

  private async drainPending(): Promise<void> {
    try {
      // The common case is an empty inbox, and drainInbox would otherwise
      // re-init the whole corpus skeleton on every single query.
      if ((await pendingInboxFiles(this.corpus)).length === 0) {
        return;
      }
      await drainInbox(this.corpus, this.index, {
        // Per-file failures no longer abort the drain, so surface them here or
        // they would pass silently — the outer catch only sees a fatal drain.
        onFileError: (name, error) =>
          this.onDrainError(new Error(`inbox file ${name} could not be ingested`, { cause: error }))
      });
    } catch (error) {
      this.onDrainError(error);
    }
  }

  async search(query: string, filters?: SearchFilters): Promise<Hit[]> {
    await this.refresh();
    return this.index.search(query, filters);
  }

  getConversation(docId: string): Promise<CorpusConversation | null> {
    return this.corpus.get(docId);
  }

  /** Recent conversations enriched with their cached summary when available. */
  async listRecent(n: number): Promise<RecentEntry[]> {
    await this.refresh();
    const recent = this.index.listRecent(n);
    const entries: RecentEntry[] = [];
    for (const row of recent) {
      const record = await this.corpus.get(row.docId);
      const summary = record?.conversation.summary?.text;
      entries.push(summary ? { ...row, summary } : row);
    }
    return entries;
  }

  /**
   * How current this corpus is. Pass `{ refresh: false }` from a caller that has
   * already drained (search/listRecent do), so a single query doesn't stat the
   * inbox twice.
   */
  async corpusStatus(options: { refresh?: boolean } = {}): Promise<CorpusStatus> {
    if (options.refresh !== false) {
      await this.refresh();
    }
    const meta = await this.corpus.readMeta();
    const lastIngestAt = meta?.lastIngestAt ?? null;
    const hoursSinceIngest =
      lastIngestAt === null ? null : Math.round(((Date.now() - lastIngestAt) / 3_600_000) * 10) / 10;
    const status: CorpusStatus = {
      root: this.corpus.paths.root,
      // From the index, not meta.json: meta's count only advances on ingest, so
      // it reads 0 for a corpus written through save_conversation. The index is
      // what queries actually see, which is the number being asked about.
      conversationCount: this.index.count(),
      lastIngestAt,
      hoursSinceIngest,
      newestConversationAt: this.index.listRecent(1)[0]?.updatedAt ?? null
    };
    if (hoursSinceIngest === null) {
      status.staleWarning =
        "No browser data has ever reached this corpus. Anything here arrived another " +
        "way; the extension's native sync may not be installed or permitted.";
    } else if (hoursSinceIngest >= STALE_AFTER_HOURS) {
      status.staleWarning =
        `No browser data has reached this corpus in ${hoursSinceIngest}h. Either the ` +
        "user has not chatted since then, or the extension to corpus sync is broken, " +
        "so results may be missing recent conversations.";
    }
    return status;
  }

  /** Validate, normalize, persist to the corpus, and index — the write path. */
  async saveConversation(payload: unknown): Promise<SaveResult> {
    const input: SaveConversationInput = saveConversationSchema.parse(payload);
    const record = normalizeSaveInput(input);
    const result = await this.corpus.upsert(record);
    if (result.outcome !== "skipped") {
      this.index.upsertConversation(record);
    }
    return { docId: result.docId, outcome: result.outcome, messageCount: record.messages.length };
  }

  close(): void {
    this.index.close();
  }
}

function noop(): void {}
